import type { ListValueInterface } from '@/interfaces/Audience.interface';

export type CampaignSendingTime = 'immediately' | 'schedule';

/**
 * Campaign wizard step ids. Mirrors the authoritative API enum `CampaignWizardStep`
 * (seg-crm-api) and the PBv2 configurator journey — keep the three in sync.
 * `preview` is a PB-only review step and is not rendered in the portal setup panel.
 */
export type CampaignWizardStepId =
  | 'details'
  | 'audience'
  | 'design-content'
  | 'schedule'
  | 'follow-up'
  | 'conversion-event'
  | 'workflow'
  | 'preview';

export interface CampaignTypeInterface {
  id: number;
  name: string;
}

export interface CampaignListTypeInterface {
  id: number;
  field_group_id: number;
  name: string;
  order: number;
}

export interface CampaignListSummaryInterface {
  id: number;
  name: string;
  list_type?: CampaignListTypeInterface;
}

export interface CampaignListFilterValueInterface {
  id: number;
  field_id: number;
  label: string;
  value: string;
}

export interface CampaignListFilterSummaryInterface {
  id: number;
  name: string;
  values?: CampaignListFilterValueInterface[];
}

export interface CampaignProductSummaryInterface {
  id?: number;
  uuid: string;
  name: string;
}

export type CampaignConversionEventTypeRef = {
  id: number;
  name: string;
};

export type CampaignConversionEventScopeRef = {
  id: number;
  name: string;
};

export const CAMPAIGN_CONVERSION_EVENT_SCOPE_NAME_SPECIFIC_PRODUCTS =
  'Specific product/s';

export interface CampaignConversionEventInterface {
  uuid: string;
  event_type_id: number;
  event_type?: CampaignConversionEventTypeRef;
  name: string;
  scope_id: number;
  scope?: CampaignConversionEventScopeRef;
  product_uuids?: string[];
  products?: CampaignProductSummaryInterface[];
}

export function isSpecificProductsConversionScope(
  scope?: CampaignConversionEventScopeRef
): boolean {
  return scope?.name === CAMPAIGN_CONVERSION_EVENT_SCOPE_NAME_SPECIFIC_PRODUCTS;
}

export type CampaignFollowUpActionTypeRef = {
  id: number;
  name: string;
};

export type CampaignFollowUpTriggerTypeRef = {
  id: number;
  name: string;
  action_type_ids?: number[];
};

export type CampaignFollowUpEmailOpensRangeRef = {
  id: number;
  name: string;
};

export const CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_EMAIL = 'Send email';
export const CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_WHATSAPP = 'Send WhatsApp';
export const CAMPAIGN_FOLLOW_UP_ACTION_NAME_SEND_SMS = 'Send SMS';

export const CAMPAIGN_FOLLOW_UP_TRIGGER_NAME_NOT_OPENED = 'Not opened';
export const CAMPAIGN_FOLLOW_UP_TRIGGER_NAME_NOT_CLICKED = 'Not clicked';
export const CAMPAIGN_FOLLOW_UP_TRIGGER_NAME_EMAIL_OPENS = 'Email opens';
export const CAMPAIGN_FOLLOW_UP_TRIGGER_NAME_NOT_CONVERTED = 'Not converted';
export const CAMPAIGN_FOLLOW_UP_TRIGGER_NAME_MESSAGE_CLICK = 'Message click';
export const CAMPAIGN_FOLLOW_UP_TRIGGER_NAME_RESPONSE_BY_RECIPIENT =
  'Response by recipient';

export interface CampaignFollowUpInterface {
  uuid: string;
  name: string;
  action_type_id: number;
  action_type?: CampaignFollowUpActionTypeRef;
  trigger_type_id: number;
  trigger_type?: CampaignFollowUpTriggerTypeRef;
  email_opens_range_id?: number | null;
  email_opens_range?: CampaignFollowUpEmailOpensRangeRef | null;
  delay_days: number;
  template_id?: string | null;
  subject_line?: string | null;
  preview_text?: string | null;
  parent_follow_up_uuid?: string | null;
  configurator_workflow_id?: string | null;
  sent_at?: string | null;
}

export interface CampaignLaunchedByUserInterface {
  id: number;
  full_name: string;
  email: string;
  uuid: string;
}

export interface CampaignSummaryInterface {
  uuid: string;
  name: string;
  status: string;
  campaign_type?: CampaignTypeInterface;
}

export interface CampaignMetricsListSummaryInterface {
  totalMessages: number;
  deliveredCount: number;
  deliveredRate: number;
  openedCount: number;
  openedRate: number;
  readCount?: number;
}

export interface CampaignInterface {
  uuid: string;
  name: string;
  status: string;
  campaign_type?: CampaignTypeInterface;
  list?: CampaignListSummaryInterface;
  list_filter?: CampaignListFilterSummaryInterface;
  email_name: string | null;
  subject_line: string | null;
  preview_text: string | null;
  message_name: string | null;
  language: string | null;
  parameter_format: string | null;
  template_id: string | null;
  sending_time: CampaignSendingTime | null;
  start_date: string | null;
  send_time: string | null;
  timezone: string | null;
  wizard_step?: CampaignWizardStepId | null;
  configurator_product_id?: string | null;
  configurator_workflow_id?: string | null;
  conversion_events?: CampaignConversionEventInterface[];
  follow_up_count?: number;
  follow_ups?: CampaignFollowUpInterface[];
  launched_at?: string | null;
  launched_by_user?: CampaignLaunchedByUserInterface | null;
  paused_at?: string | null;
  paused_by_user?: CampaignLaunchedByUserInterface | null;
  metrics_summary?: CampaignMetricsListSummaryInterface | null;
  created_at: string | null;
  updated_at: string | null;
}

export type CampaignConversionEventPayload = {
  event_type_id: number;
  name: string;
  scope_id: number;
  product_uuids?: string[];
};

export interface CampaignCreatePayload {
  name: string;
  campaign_type_id: number;
  list_id?: number | null;
  list_filter_id?: number | null;
  status?: string;
  email_name?: string | null;
  subject_line?: string | null;
  preview_text?: string | null;
  message_name?: string | null;
  language?: string | null;
  parameter_format?: string | null;
  template_id?: string | null;
  sending_time?: CampaignSendingTime | null;
  start_date?: string | null;
  send_time?: string | null;
  timezone?: string | null;
  conversion_events?: CampaignConversionEventPayload[];
}

export type CampaignLaunchPayload = {
  launch_acknowledged: true;
};

export type CampaignLaunchReadinessSection =
  | 'details'
  | 'content'
  | 'schedule'
  | 'workflow'
  | 'follow_ups'
  | 'conversion';

export interface CampaignLaunchReadinessItem {
  section: CampaignLaunchReadinessSection;
  label: string;
  complete: boolean;
  message?: string;
}

export interface CampaignLaunchReadinessResult {
  ready: boolean;
  sections: CampaignLaunchReadinessItem[];
}

export type CampaignUpdatePayload = Partial<CampaignCreatePayload> & {
  wizard_step?: CampaignWizardStepId | null;
  configurator_product_id?: string | null;
  configurator_workflow_id?: string | null;
};

export type CampaignEventProvider = 'twilio' | 'sendgrid' | 'ses';

export type CampaignEventChannel = 'email' | 'sms' | 'whatsapp';

export type CampaignEventType =
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'undelivered'
  | 'failed'
  | 'blocked'
  | 'dropped'
  | 'opened'
  | 'clicked'
  | 'bounced'
  | 'spam_report'
  | 'unsubscribed'
  | 'suppressed'
  | 'read';

export interface CampaignStatusEntryInterface {
  eventType: CampaignEventType;
  providerEventType: string;
  providerEventId?: string;
  errorCode?: string;
  rawFields: Record<string, unknown>;
  occurredAt: string;
}

export interface CampaignMetricsRatesInterface {
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  unsubscribeRate: number;
  failureRate: number;
  conversionRate?: number;
}

export interface CampaignMetricsBreakdownInterface
  extends Partial<Record<CampaignEventType, number>> {
  quoteConverted?: number;
  policyConverted?: number;
  totalConversions?: number;
}

export interface CampaignEngagementOverTimePoint {
  date: string;
  opens: number;
  clicks: number;
  conversions: number;
}

export interface CampaignMetricsInterface {
  campaignId: string;
  totalMessages: number;
  breakdown: CampaignMetricsBreakdownInterface;
  rates: CampaignMetricsRatesInterface;
  channels: Record<CampaignEventChannel, number>;
  engagementOverTime?: CampaignEngagementOverTimePoint[];
}

export interface CampaignMessageInterface {
  provider: CampaignEventProvider;
  providerMessageId: string;
  channel: CampaignEventChannel;
  currentStatus: CampaignEventType;
  statusHistory: CampaignStatusEntryInterface[];
  recipient: string;
  workflowExecutionId?: string;
  workflowId?: string;
  productId?: string;
  createdAt: string;
  updatedAt: string;
  audienceListId: string;
  campaignId: string;
  isFollowUp?: boolean;
  followUpWorkflowId?: string;
}

export type CampaignMetricsParams = {
  scope?: 'all';
  followUpWorkflowId?: string;
};

export type CampaignMessageParams = {
  recipient: string;
  followUpWorkflowId?: string;
};

export interface CampaignRecipientEngagementMetricInterface {
  total: number;
  firstAt: string;
  lastAt: string;
}

export interface CampaignRecipientEngagementInterface {
  recipient?: string | null;
  currentStatus?: CampaignEventType | null;
  status?: string | null;
  opens?: number | null;
  clicks?: number | null;
  opensEngagement?: CampaignRecipientEngagementMetricInterface | null;
  clicksEngagement?: CampaignRecipientEngagementMetricInterface | null;
  spamReported?: 'Yes' | 'No' | null;
  unsubscribed?: 'Yes' | 'No' | null;
  replied?: 'Yes' | 'No' | null;
  blocked?: 'Yes' | 'No' | null;
  optOut?: 'Yes' | 'No' | null;
}

export type CampaignRecipientRowInterface = ListValueInterface & {
  engagement: CampaignRecipientEngagementInterface | null;
};

export type CampaignRecipientsParams = CampaignMetricsParams;
